import { createElementId } from '../utils/elementPatch';
import { paintFromHex } from '../utils/paintUtils';
import { measureTextBox } from '../utils/textMeasure';

const DEFAULT_SIZES = {
  shape: { w: 320, h: 180 },
  line: { w: 320, h: 6 },
  arrow: { w: 320, h: 6 },
  frame: { w: 360, h: 260 },
  graphic: { w: 160, h: 160 },
  table: { w: 520, h: 320 },
  chart: { w: 520, h: 320 },
  grid: { w: 640, h: 640 },
  form: { w: 360, h: 200 },
};

const clampToArtboard = (x, y, w, h, artboardWidth, artboardHeight) => {
  const maxX = Math.max(0, artboardWidth - w);
  const maxY = Math.max(0, artboardHeight - h);
  return {
    x: Math.min(Math.max(x, 0), maxX),
    y: Math.min(Math.max(y, 0), maxY),
  };
};

const getMaxZ = (elements) =>
  Math.max(0, ...elements.map((el) => el.zIndex || 0));

const parseViewBox = (viewBox) => {
  if (typeof viewBox !== 'string') {
    return { minX: 0, minY: 0, width: 100, height: 100 };
  }
  const parts = viewBox
    .trim()
    .split(/\s+/)
    .map((part) => Number(part));
  if (parts.length !== 4 || parts.some((value) => Number.isNaN(value))) {
    return { minX: 0, minY: 0, width: 100, height: 100 };
  }
  return {
    minX: parts[0],
    minY: parts[1],
    width: parts[2] || 100,
    height: parts[3] || 100,
  };
};

const mapShapeType = (payload = {}, item = {}) => {
  if (item.kind === 'line' || payload.type === 'line') return 'line';
  if (item.kind === 'arrow' || payload.type === 'arrow') return 'arrow';
  if (item.kind === 'frame' || payload.type === 'frame') return 'frame';
  const shapeKind = payload.shapeKind;
  if (['circle', 'triangle', 'diamond', 'star', 'speech', 'badge', 'blob', 'graphic'].includes(shapeKind)) {
    return shapeKind;
  }
  return 'rect';
};

const buildShapeElement = ({
  shapeType,
  x,
  y,
  w,
  h,
  fill,
  stroke,
  strokeWidth,
  strokeStyle,
  lineCap,
  arrowHead,
  arrowHeadSize,
  radius,
  fillEnabled,
  strokeEnabled,
  svg,
  zIndex,
}) => {
  const fillHex = fill || '#FFFFFF';
  const strokeHex = stroke || '#111111';
  return {
    id: createElementId(),
    type: 'shape',
    shapeType,
    x,
    y,
    w,
    h,
    rotation: 0,
    opacity: 1,
    locked: false,
    zIndex,
    fill: fillHex,
    stroke: strokeHex,
    strokeWidth: strokeWidth ?? 2,
    strokeStyle: strokeStyle || 'solid',
    lineCap: lineCap || 'round',
    arrowHead: arrowHead || 'none',
    arrowHeadSize: arrowHeadSize || 16,
    radius: radius ?? 0,
    flipX: false,
    flipY: false,
    fillEnabled: fillEnabled ?? true,
    strokeEnabled: strokeEnabled ?? true,
    svg,
    style: {
      fillPaint: paintFromHex(fillHex),
      strokePaint: paintFromHex(strokeHex),
    },
  };
};

const buildFrameElement = ({ payload, x, y, w, h, zIndex }) => {
  const viewBox = payload.viewBox || '0 0 100 100';
  const viewBoxMeta = parseViewBox(viewBox);
  const shapeType = payload.shapeType || (payload.pathD ? 'path' : 'rect');
  const cornerRadius = payload.cornerRadius ?? payload.radius ?? 12;
  const backgroundHex = payload.defaultFill || '#F6F2E8';
  return {
    id: createElementId(),
    type: 'frame',
    x,
    y,
    w,
    h,
    rotation: 0,
    opacity: 1,
    locked: false,
    zIndex,
    backgroundPaint: paintFromHex(backgroundHex),
    frame: {
      frameId: payload.frameId || createElementId(),
      shapeType,
      viewBox,
      viewBoxWidth: viewBoxMeta.width,
      viewBoxHeight: viewBoxMeta.height,
      pathD: payload.pathD || '',
      cornerRadius,
      border: {
        enabled: false,
        color: '#FFFFFF',
        width: 0,
      },
      shadow: {
        enabled: false,
        blur: 0,
        opacity: 0,
        x: 0,
        y: 0,
      },
    },
    content: null,
  };
};

const buildTextElement = ({
  x,
  y,
  w,
  text,
  fontSize = 22,
  fontWeight = 500,
  color = '#111111',
  align = 'left',
  zIndex,
  autoWidth = false,
}) => {
  const measure = measureTextBox({
    text,
    wrapWidth: autoWidth ? undefined : w,
    autoWidth,
    fontSize,
    fontFamily: 'Inter, sans-serif',
    fontWeight,
    fontStyle: 'normal',
    lineHeight: 1.2,
    letterSpacing: 0,
    paddingX: 0,
    paddingY: 0,
    minW: 40,
  });
  return {
    id: createElementId(),
    type: 'text',
    x,
    y,
    w: autoWidth ? measure.tightW : w,
    h: measure.tightH,
    rotation: 0,
    opacity: 1,
    locked: false,
    zIndex,
    text,
    fontFamily: 'Inter, sans-serif',
    fontSize,
    fontWeight,
    fontStyle: 'normal',
    underline: false,
    color,
    align,
    lineHeight: 1.2,
    letterSpacing: 0,
    paddingX: 0,
    paddingY: 0,
    autoWidth,
    autoHeight: true,
    minW: 40,
  };
};

const buildImageElement = ({ src, x, y, w, h, zIndex }) => ({
  id: createElementId(),
  type: 'image',
  src,
  x,
  y,
  w,
  h,
  rotation: 0,
  opacity: 1,
  locked: false,
  zIndex,
});

const fitSize = (naturalW, naturalH, maxW, maxH, min = 120) => {
  if (!naturalW || !naturalH) {
    return { w: Math.max(min, Math.min(maxW, 520)), h: Math.max(min, Math.min(maxH, 360)) };
  }
  const scale = Math.min(maxW / naturalW, maxH / naturalH, 1);
  const w = Math.max(min, Math.round(naturalW * scale));
  const h = Math.max(min, Math.round(naturalH * scale));
  return { w, h };
};

const chartSvg = (type = 'bar') => {
  switch (type) {
    case 'line':
      return '<svg viewBox="0 0 100 100" fill="none" stroke="currentColor" stroke-width="6" stroke-linecap="round" stroke-linejoin="round"><polyline points="14,70 38,44 60,56 86,30"/></svg>';
    case 'pie':
      return '<svg viewBox="0 0 100 100" fill="currentColor"><path d="M50 50 L50 16 A34 34 0 0 1 84 50 Z"/><path d="M50 50 L84 50 A34 34 0 1 1 50 16 Z" fill-opacity="0.35"/></svg>';
    case 'donut':
      return '<svg viewBox="0 0 100 100" fill="none" stroke="currentColor" stroke-width="16" stroke-linecap="round"><circle cx="50" cy="50" r="28" stroke-opacity="0.35"/><path d="M50 22 A28 28 0 0 1 78 50" /></svg>';
    default:
      return '<svg viewBox="0 0 100 100" fill="currentColor"><rect x="18" y="52" width="12" height="30" rx="3"/><rect x="40" y="40" width="12" height="42" rx="3"/><rect x="62" y="30" width="12" height="52" rx="3"/></svg>';
  }
};

const buildTableElements = ({ rows, cols, width, height, origin, zIndexStart }) => {
  const elements = [];
  const groupId = createElementId();
  const strokeColor = '#D7D2C8';
  const fillColor = '#FFFFFF';
  const lineThickness = 2;
  const cellW = width / cols;
  const cellH = height / rows;
  let z = zIndexStart;

  elements.push(
    buildShapeElement({
      shapeType: 'rect',
      x: origin.x,
      y: origin.y,
      w: width,
      h: height,
      fill: fillColor,
      stroke: strokeColor,
      strokeWidth: 2,
      radius: 8,
      zIndex: z++,
      fillEnabled: true,
      strokeEnabled: true,
    }),
  );

  for (let r = 1; r < rows; r += 1) {
    elements.push(
      buildShapeElement({
        shapeType: 'rect',
        x: origin.x,
        y: origin.y + r * cellH - lineThickness / 2,
        w: width,
        h: lineThickness,
        fill: strokeColor,
        stroke: strokeColor,
        strokeWidth: 0,
        radius: 0,
        zIndex: z++,
        fillEnabled: true,
        strokeEnabled: false,
      }),
    );
  }

  for (let c = 1; c < cols; c += 1) {
    elements.push(
      buildShapeElement({
        shapeType: 'rect',
        x: origin.x + c * cellW - lineThickness / 2,
        y: origin.y,
        w: lineThickness,
        h: height,
        fill: strokeColor,
        stroke: strokeColor,
        strokeWidth: 0,
        radius: 0,
        zIndex: z++,
        fillEnabled: true,
        strokeEnabled: false,
      }),
    );
  }

  return {
    elements: elements.map((el) => ({ ...el, groupId })),
    groupId,
    nextZ: z,
  };
};

const buildGridElements = ({ layout, width, height, origin, zIndexStart }) => {
  const layouts = {
    '2x2': { rows: 2, cols: 2 },
    '3x1': { rows: 1, cols: 3 },
    '1+2': { rows: 2, cols: 2, custom: true },
    '3x3': { rows: 3, cols: 3 },
  };
  const config = layouts[layout] || layouts['2x2'];
  const elements = [];
  const groupId = createElementId();
  let z = zIndexStart;
  const gap = 10;
  const strokeColor = '#C9A962';

  if (layout === '1+2') {
    const leftW = (width - gap) * 0.45;
    const rightW = width - gap - leftW;
    const fullH = height;
    const halfH = (height - gap) / 2;
    const rects = [
      { x: origin.x, y: origin.y, w: leftW, h: fullH },
      { x: origin.x + leftW + gap, y: origin.y, w: rightW, h: halfH },
      { x: origin.x + leftW + gap, y: origin.y + halfH + gap, w: rightW, h: halfH },
    ];
    rects.forEach((rect) => {
      elements.push(
        buildShapeElement({
          shapeType: 'rect',
          x: rect.x,
          y: rect.y,
          w: rect.w,
          h: rect.h,
          fill: '#FFFFFF',
          stroke: strokeColor,
          strokeWidth: 2,
          radius: 10,
          zIndex: z++,
          fillEnabled: false,
          strokeEnabled: true,
        }),
      );
    });
  } else {
    const rows = config.rows;
    const cols = config.cols;
    const cellW = (width - gap * (cols - 1)) / cols;
    const cellH = (height - gap * (rows - 1)) / rows;
    for (let r = 0; r < rows; r += 1) {
      for (let c = 0; c < cols; c += 1) {
        elements.push(
          buildShapeElement({
            shapeType: 'rect',
            x: origin.x + c * (cellW + gap),
            y: origin.y + r * (cellH + gap),
            w: cellW,
            h: cellH,
            fill: '#FFFFFF',
            stroke: strokeColor,
            strokeWidth: 2,
            radius: 10,
            zIndex: z++,
            fillEnabled: false,
            strokeEnabled: true,
          }),
        );
      }
    }
  }

  return {
    elements: elements.map((el) => ({ ...el, groupId })),
    groupId,
    nextZ: z,
  };
};

const buildFormPreset = ({ payload, origin, zIndexStart }) => {
  const elements = [];
  const groupId = createElementId();
  let z = zIndexStart;

  payload.elements.forEach((el) => {
    if (el.type === 'shape') {
      const shapeType = el.shapeType || 'rect';
      const next = buildShapeElement({
        shapeType,
        x: origin.x + el.x * payload.width,
        y: origin.y + el.y * payload.height,
        w: el.w * payload.width,
        h: el.h * payload.height,
        fill: el.fill || '#FFFFFF',
        stroke: el.stroke || '#111111',
        strokeWidth: el.strokeWidth ?? 1,
        radius: el.radius ?? 0,
        zIndex: z++,
        fillEnabled: true,
        strokeEnabled: (el.strokeWidth ?? 1) > 0,
      });
      elements.push({ ...next, groupId });
      return;
    }
    if (el.type === 'text') {
      const next = buildTextElement({
        x: origin.x + el.x * payload.width,
        y: origin.y + el.y * payload.height,
        w: el.w * payload.width,
        text: el.text,
        fontSize: el.fontSize ?? 18,
        fontWeight: el.fontWeight ?? 500,
        color: el.color || '#111111',
        align: el.align || 'left',
        zIndex: z++,
        autoWidth: false,
      });
      elements.push({ ...next, groupId });
    }
  });

  return { elements, groupId, nextZ: z };
};

export const insertCatalogItem = ({
  catalogItem,
  existingElements,
  center,
  artboardWidth,
  artboardHeight,
}) => {
  if (!catalogItem) return null;
  const items = Array.isArray(existingElements) ? existingElements : [];
  const maxZ = getMaxZ(items);
  let zIndex = maxZ + 1;
  const newElements = [];

  const payload = catalogItem.payload || {};
  const kind = catalogItem.kind || payload.type;
  const baseSize = DEFAULT_SIZES[kind] || DEFAULT_SIZES.shape;
  const width = Number(payload.width) || baseSize.w;
  const height = Number(payload.height) || baseSize.h;
  const baseOrigin = clampToArtboard(
    center.x - width / 2,
    center.y - height / 2,
    width,
    height,
    artboardWidth,
    artboardHeight,
  );

  if (kind === 'frame') {
    const next = buildFrameElement({
      payload,
      x: baseOrigin.x,
      y: baseOrigin.y,
      w: width,
      h: height,
      zIndex: zIndex++,
    });
    newElements.push(next);
  } else if (kind === 'shape' || kind === 'line' || kind === 'arrow') {
    const shapeType = mapShapeType(payload, catalogItem);
    const fillColor = payload.defaultFill || '#FFFFFF';
    const strokeColor = payload.defaultStroke || '#111111';
    const fillEnabled = !['line', 'arrow'].includes(shapeType);
    const strokeEnabled = true;
    const radius = payload.defaultRadius ?? payload.radius ?? (shapeType === 'circle' ? width / 2 : 0);
    const next = buildShapeElement({
      shapeType,
      x: baseOrigin.x,
      y: baseOrigin.y,
      w: width,
      h: height,
      fill: fillColor,
      stroke: strokeColor,
      strokeWidth: payload.defaultStrokeWidth ?? payload.strokeWidth ?? 2,
      strokeStyle: payload.dash || payload.strokeStyle || 'solid',
      lineCap: payload.lineCap || 'round',
      arrowHead: payload.arrowHead || (shapeType === 'arrow' ? 'end' : 'none'),
      arrowHeadSize: payload.arrowHeadSize || 16,
      radius,
      fillEnabled,
      strokeEnabled,
      zIndex: zIndex++,
    });
    newElements.push(next);
  } else if (kind === 'graphic') {
    const tint = payload.defaultColor || '#111111';
    const svg = payload.svg || catalogItem.preview?.inlineSvg;
    const next = buildShapeElement({
      shapeType: 'graphic',
      x: baseOrigin.x,
      y: baseOrigin.y,
      w: width,
      h: height,
      fill: tint,
      stroke: tint,
      strokeWidth: 0,
      radius: 0,
      fillEnabled: true,
      strokeEnabled: false,
      svg,
      zIndex: zIndex++,
    });
    newElements.push(next);
  } else if (kind === 'photo' || payload.type === 'image') {
    const maxW = artboardWidth * 0.6;
    const maxH = artboardHeight * 0.6;
    const naturalW = payload.naturalW || 1400;
    const naturalH = payload.naturalH || 933;
    const size = fitSize(naturalW, naturalH, maxW, maxH, 120);
    const origin = clampToArtboard(
      center.x - size.w / 2,
      center.y - size.h / 2,
      size.w,
      size.h,
      artboardWidth,
      artboardHeight,
    );
    newElements.push(
      buildImageElement({
        src: payload.src || catalogItem.preview?.src,
        x: origin.x,
        y: origin.y,
        w: size.w,
        h: size.h,
        zIndex: zIndex++,
      }),
    );
  } else if (kind === 'table') {
    const result = buildTableElements({
      rows: payload.rows || 3,
      cols: payload.cols || 3,
      width,
      height,
      origin: baseOrigin,
      zIndexStart: zIndex,
    });
    newElements.push(...result.elements);
    zIndex = result.nextZ;
  } else if (kind === 'chart') {
    const svg = chartSvg(payload.chartType);
    newElements.push(
      buildShapeElement({
        shapeType: 'graphic',
        x: baseOrigin.x,
        y: baseOrigin.y,
        w: width,
        h: height,
        fill: '#111111',
        stroke: '#111111',
        strokeWidth: 0,
        radius: 0,
        fillEnabled: true,
        strokeEnabled: false,
        svg,
        zIndex: zIndex++,
      }),
    );
  } else if (kind === 'grid') {
    const result = buildGridElements({
      layout: payload.layout,
      width,
      height,
      origin: baseOrigin,
      zIndexStart: zIndex,
    });
    newElements.push(...result.elements);
    zIndex = result.nextZ;
  } else if (kind === 'form' && payload.type === 'groupPreset') {
    const presetWidth = payload.width || DEFAULT_SIZES.form.w;
    const presetHeight = payload.height || DEFAULT_SIZES.form.h;
    const origin = clampToArtboard(
      center.x - presetWidth / 2,
      center.y - presetHeight / 2,
      presetWidth,
      presetHeight,
      artboardWidth,
      artboardHeight,
    );
    const result = buildFormPreset({
      payload: { ...payload, width: presetWidth, height: presetHeight },
      origin,
      zIndexStart: zIndex,
    });
    newElements.push(...result.elements);
    zIndex = result.nextZ;
  } else {
    return null;
  }

  if (!newElements.length) return null;
  return {
    elements: [...items, ...newElements],
    selectedIds: newElements.map((el) => el.id),
  };
};
